import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

export default function AdminStoriesPage() {
  const [stories, setStories] = useState([]);
  const [draft, setDraft] = useState({ title: "", summary: "", imageUrl: "" });
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addImageFile, setAddImageFile] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");

  async function load() {
    const res = await adminService.listStories();
    if (!res.ok) return setError(res.error || "Failed to fetch stories");
    setStories(res.data?.items || []);
  }
  useEffect(() => { load(); }, []);

  function clearAddImage() {
    if (addImagePreview && addImagePreview.startsWith("blob:")) URL.revokeObjectURL(addImagePreview);
    setAddImageFile(null);
    setAddImagePreview("");
  }

  function clearEditImage() {
    if (editImagePreview && editImagePreview.startsWith("blob:")) URL.revokeObjectURL(editImagePreview);
    setEditImageFile(null);
    setEditImagePreview("");
  }

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Stories" highlight="editor." description="Add, edit, and remove story entries." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}
          <div><Button onClick={() => { clearAddImage(); setAddOpen(true); }}>Add Story</Button></div>
          <div className="grid md:grid-cols-2 gap-6">
          {stories.map((s) => (
            <Card key={s._id}>
              <CardHeader><CardTitle>{s.title}</CardTitle></CardHeader>
              <CardContent className="grid gap-2">
                {s.imageUrl ? (
                  <img
                    src={s.imageUrl}
                    alt={s.title}
                    className="h-40 w-full object-cover rounded-xl border border-border bg-background"
                  />
                ) : (
                  <div className="text-sm text-muted-foreground">No image</div>
                )}
                <p className="text-sm text-muted-foreground">{s.summary || "-"}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditing({ ...s }); setEditImageFile(null); clearEditImage(); setEditOpen(true); }}>Edit</Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const r = await adminService.deleteStory(s._id);
                      if (!r.ok) {
                        setError(r.error);
                        toast.error(r.error || "Delete failed");
                      } else {
                        toast.success("Story deleted");
                        await load();
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      </PageSection>
      <FormModal open={addOpen} title="Add Story" onClose={() => { clearAddImage(); setAddOpen(false); }}>
        <div className="grid gap-2">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={draft.title} onChange={(e)=>setDraft((p)=>({...p,title:e.target.value}))}/>
          <textarea className="min-h-20 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Summary" value={draft.summary} onChange={(e)=>setDraft((p)=>({...p,summary:e.target.value}))}/>
          <div className="grid gap-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Story image</div>
            <input
              type="file"
              accept="image/*"
              className="h-10 px-3 py-2 rounded-xl border border-border bg-background text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-accent/10 file:px-3 file:py-1 file:text-accent"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (!file) return clearAddImage();
                clearAddImage();
                const url = URL.createObjectURL(file);
                setAddImageFile(file);
                setAddImagePreview(url);
              }}
            />
            {addImagePreview ? (
              <img src={addImagePreview} alt="Preview" className="h-40 w-full object-cover rounded-xl border border-border bg-background" />
            ) : (
              <div className="text-sm text-muted-foreground">No image selected</div>
            )}
          </div>
          <div><Button size="sm" onClick={async()=>{try{let imageUrl = draft.imageUrl || ""; if (addImageFile) { const up = await adminService.uploadImage(addImageFile); if (!up.ok) throw new Error(up.error || "Upload failed"); imageUrl = up.data.url; } const r=await adminService.createStory({ ...draft, imageUrl }); if(!r.ok) return toast.error(r.error || "Create failed"); toast.success("Story created"); clearAddImage(); setDraft({title:"",summary:"",imageUrl:""}); setAddOpen(false); await load(); } catch (e) { toast.error("Save failed", { description: e?.message || "Try again." }); }} }>Save Story</Button></div>
        </div>
      </FormModal>
      <FormModal open={editOpen} title="Edit Story" onClose={() => { clearEditImage(); setEditOpen(false); }}>
        <div className="grid gap-2">
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={editing?.title || ""} onChange={(e)=>setEditing((p)=>({...(p||{}),title:e.target.value}))}/>
          <textarea className="min-h-20 px-3 py-2 rounded-xl border border-border bg-background text-sm" value={editing?.summary || ""} onChange={(e)=>setEditing((p)=>({...(p||{}),summary:e.target.value}))}/>
          <div className="grid gap-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Replace image (optional)</div>
            <input
              type="file"
              accept="image/*"
              className="h-10 px-3 py-2 rounded-xl border border-border bg-background text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-accent/10 file:px-3 file:py-1 file:text-accent"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (!file) return clearEditImage();
                clearEditImage();
                const url = URL.createObjectURL(file);
                setEditImageFile(file);
                setEditImagePreview(url);
              }}
            />
            {editImagePreview ? (
              <img src={editImagePreview} alt="Preview" className="h-40 w-full object-cover rounded-xl border border-border bg-background" />
            ) : editing?.imageUrl ? (
              <img src={editing.imageUrl} alt="Current" className="h-40 w-full object-cover rounded-xl border border-border bg-background" />
            ) : (
              <div className="text-sm text-muted-foreground">No image</div>
            )}
          </div>
          <div><Button size="sm" onClick={async()=>{try{if(!editing?._id) return; let imageUrl = editing.imageUrl || ""; if (editImageFile) { const up = await adminService.uploadImage(editImageFile); if (!up.ok) throw new Error(up.error || "Upload failed"); imageUrl = up.data.url; } const r=await adminService.updateStory(editing._id,{title:editing.title,summary:editing.summary,imageUrl,isPublished:editing.isPublished!==false}); if(!r.ok) return toast.error(r.error || "Update failed"); toast.success("Story updated"); clearEditImage(); setEditOpen(false); await load(); } catch (e) { toast.error("Update failed", { description: e?.message || "Try again." }); }} }>Update Story</Button></div>
        </div>
      </FormModal>
    </PageShell>
  );
}

